/**
 *
 * @module component/extender/get
 * @author Adam Pirani [adam.pirani@mlb.com]
 * @description Extender 'get' helpers
 *
 */

import get from 'lodash-es/get';
import getStatHelpers from './getStat';
const { getStat, getPitcherStatLine, getPlayerImage, getPlayerUrl } = getStatHelpers;
import helpers from './../helpers';
const { i18n } = helpers;
const SPORTID_MLB = '1';

let INNING_STATE_MAP = null;
const DECISION_TO_ABBREV = {
    winner: i18n('stats_wins_abbrev', 'W'),
    loser: i18n('stats_losses_abbrev', 'L'),
    save: i18n('stats_saves_abbrev', 'SV')
};

function getDate(opts = {}, gameData = {}) {

    let { mlbMoment } = opts;
    let { gameDate } = gameData;

    let moment = mlbMoment.getMomentTimezone();
    let bamMomentLocales = mlbMoment.getBamMomentLocales();
    let isTBD = get(gameData, 'status.startTimeTBD');

    if (isTBD) {

        gameDate = gameDate.substr(0, 10);

    }

    return moment(gameDate).format(bamMomentLocales.get_ddd_MMM_D());

}

/**
 * @function getDescription
 * @desc Get a game's descriptor (ie: Home Opener / Jackie Robinson Day)
 * @param gameData
 * @returns {String}
 */
function getDescription(gameData = {}) {

    let { gameType, description } = gameData;

    let desc = '';

    if (gameType === 'R') { // Regular season game

        desc = description || '';

    }

    return desc;

}

function getGameTime(opts = {}, gameData = {}, status = {}, isPostseasonPush = false) {

    if (!INNING_STATE_MAP) {
        INNING_STATE_MAP = {
            Top: i18n('game_status_top', 'Top'),
            Middle: i18n('game_status_mid', 'Mid'),
            Bottom: i18n('game_status_bottom_abbrev', 'Bot'),
            End: i18n('game_status_end', 'End')
        };
    }

    let { mlbMoment, mode, teamId } = opts;
    let { gameDate, linescore } = gameData;
    let { abstract = {} } = status;
    let { isCancelled, isPostponed, isDelayed, isInstantReplay, isManagerChallenge } = abstract;
    let { isUmpireReview, isWarmup, isSuspended, isPreview, isTBD } = abstract;
    let { isDoubleHeader, isLive, isFinal } = abstract;
    const isTeamContext = !!teamId;
    let gameTime = '';


    if (isCancelled) {

        return i18n('game_status_cancelled', 'Cancelled');

    } else if (isPostponed) {

        return i18n('game_status_postponed_abbrev', 'PPD');

    } else if (isDelayed) {

        return i18n('game_status_delayed', 'Delayed');

    } else if (isInstantReplay) {

        return i18n('game_status_replay', 'Replay');

    } else if (isManagerChallenge && !isPostseasonPush) {

        return i18n('game_status_challenge', 'Challenge');

    } else if (isUmpireReview) {

        return i18n('game_status_review', 'Review');

    } else if (isWarmup) {

        return i18n('game_status_warmup', 'Warmup');

    } else if (isSuspended) {

        return i18n('game_status_susp_abbrev', 'Susp');

    } else if (isPreview) {

        if (isTBD) {

            if (isDoubleHeader) {

                let { gameNumber = 1 } = gameData;

                return i18n('game_time_gameX', 'Game {{gameNumber}}').replace('{{gameNumber}}', gameNumber);

            } else {

                return i18n('game_time_tbd', 'TBD');

            }

        } else {

            let moment = mlbMoment.getMomentTimezone();
            let mDay = moment(gameDate);
            let showTz = mode !== 'game-selector';
            let tzString = '';

            // Club scores/MIG/Mediawall use 3 digit timezones
            if (isTeamContext) {

                tzString = mDay.format('z');

            } else {

                let tzObj = mlbMoment.getTimeZoneDictionary(gameDate);
                tzString = i18n(tzObj.key, tzObj.fallback);

            }

            gameTime = mDay.format('LT');

            if (showTz) {

                gameTime = `${gameTime} ${tzString}`;

            }

            return gameTime;

        }

    } else if (isLive) {

        let inningStateText = INNING_STATE_MAP[linescore.inningState] || '';
        let inningOrdinal = linescore.currentInningOrdinal || '';
        let currentInning = get(linescore, 'currentInning');
        let challengingText = '';

        if (isManagerChallenge) {
            challengingText = (!abstract.isFreeGame) ? `/${i18n('game_status_challenge', 'Challenge')}`
                : `/${i18n('game_status_chal', 'Chal')}`;
        }
        const ingTextPostSeasonPush = `${inningStateText} ${currentInning}${challengingText}`;
        const ingText = `${inningStateText} ${inningOrdinal}`;

        return (isPostseasonPush) ? ingTextPostSeasonPush : ingText;

    } else if (isFinal) {

        let currentInning = get(linescore, 'currentInning');
        let scheduledInnings = get(linescore, 'scheduledInnings', 9);

        if (currentInning > scheduledInnings || abstract.isCompletedEarly) {

            gameTime = i18n('game_status_final_abbrev', 'F/{{numInnings}}').replace('{{numInnings}}', currentInning);

        } else {

            gameTime = i18n('game_status_final', 'Final');

        }

        return gameTime;
    }

    return gameTime;

}

function getGameTimePostseasonPush(opts = {}, gameData = {}, status = {}) {

    return getGameTime(opts, gameData, status, true);
}

/**
 * @function getHomeRuns
 * @description Get home runs for each team, displayed in final games
 * @param opts
 * @param data
 * @returns {{away: {hasRuns: boolean, runs: Array, players: {}}, home: {hasRuns: boolean, runs: Array, players: {}}, hasRuns: boolean}}
 */
function getHomeRuns(opts = {}, data = {}) {

    let { mlbLinker } = opts;

    const { teams, sport } = data;

    let away = {
        hasRuns: false,
        runs: [],
        tempRuns: {},
        players: {},
        teamAbbrev: get(teams, 'away.team.abbreviation')
    };

    let home = {
        hasRuns: false,
        runs: [],
        tempRuns: {},
        players: {},
        teamAbbrev: get(teams, 'home.team.abbreviation')
    };

    let { homeRuns = [] } = data;

    homeRuns.forEach((play, idx) => {

        let half = get(play, 'about.halfInning');
        let team = away;

        if (half === 'bottom') {

            team = home;

        }

        team.hasRuns = true;

        let { matchup } = play;
        let { batter } = matchup;
        let { id, initLastName, stats, fullName } = batter;

        let gameStats = getStat(stats, 'gameLog', 'hitting');
        let seasonStats = getStat(stats, 'statsSingleSeason', 'hitting');
        let gameHRs = get(gameStats, 'stats.homeRuns');
        let seasonHRs = get(seasonStats, 'stats.homeRuns');
        let runNumber = '(-)';
        let tempRun = team.tempRuns[id];
        let totalRuns = 0;
        let tempIdx;
        let tempRunNumber;

        if (gameHRs !== undefined && seasonHRs !== undefined) {

            if (!team.players[id]) {

                team.players[id] = seasonHRs - gameHRs + 1;

            } else {

                team.players[id] = team.players[id] + 1;

            }

            if (tempRun) {

                totalRuns = tempRun.totalRuns + 1;
                tempIdx = tempRun.index;
                tempRunNumber = `${tempRun.runNumber}, ${team.players[id]}`;

            } else {

                totalRuns = 1;
                tempIdx = idx;
                tempRunNumber = team.players[id];

            }

            team.tempRuns[id] = {
                index: tempIdx,
                totalRuns: totalRuns,
                runNumber: tempRunNumber
            };

            totalRuns = team.tempRuns[id].totalRuns;
            runNumber = `(${team.tempRuns[id].runNumber})`;

        }

        if (team.runs[team.tempRuns[id].index]) {

            const teamRunsLen = team.runs.length;

            for (let i = teamRunsLen; i--;) {
                if (team.runs[i].id === id) {

                    team.runs[i].totalRuns = totalRuns;
                    team.runs[i].runNumber = runNumber;
                    break;

                }
            }

        } else {

            const playerScores = {
                id,
                initLastName,
                fullName,
                totalRuns,
                runNumber,
                playerUrl: getPlayerUrl(mlbLinker, batter, sport)
            };

            team.runs.push(playerScores);

        }

    });

    return {
        away,
        home,
        hasRuns: away.hasRuns || home.hasRuns
    };

}

/**
 * @function getChallengingTeam
 * @description If not game Selector, return the challenging team name
 * @param gameData
 * @returns {String}
 */
function getChallengingTeam(opts = {}, gameData = {}) {

    const { gameUtils, mode = 'responsive' } = opts;
    let challengingTeamAbbrev = '';

    if (mode === 'MIG' || mode === 'responsive' || mode === 'minisb') {

        const gameReview = get(gameData, 'review', '');
        const bamTeamData = gameUtils.getBamTeamData ? gameUtils.getBamTeamData() : '';
        const challengingTeam = (gameReview.hasChallenges && gameReview.challengingTeam && gameReview.challengingTeam.id)
            ? (bamTeamData ? bamTeamData.getTeam(gameReview.challengingTeam.id) : '')
            : null;

        challengingTeamAbbrev = challengingTeam
            ? challengingTeam.abbreviation
            : '';

    }

    return challengingTeamAbbrev;

}

/**
 * @function getFlag
 * @description Get whether a game is live-lookin, perfect game, or no hitter
 * @param opts
 * @param data Game data
 * @returns {{isFlag: *, textShort: string, textLong: string, type: string}}
 */
function getFlag(opts = {}, data = {}) {
    const { mlbLinker, mode, liveLookInSponsor } = opts;
    const { flags = {} } = data;
    const { noHitter, perfectGame } = flags;
    const liveLook = get(data, 'liveLookin.mediaPlaybackId');
    const isFlag = noHitter || perfectGame || liveLook;
    let textShort = '';
    let textLong = '';
    let type = '';
    let url = '';

    if (liveLook) {

        textShort = i18n('livelookin_abbrev', 'Live Look');

        if (liveLookInSponsor) {

            textLong = i18n('livelookin_sponsor', 'Live Look In Presented by {{sponsor}}')
                .replace('{{sponsor}}', liveLookInSponsor);

        } else {

            textLong = i18n('livelookin', 'Live Look In');

        }

        type = 'live-lookin';
        url = mlbLinker && mlbLinker.getLiveLookIn(liveLook) || '';

    } else if (perfectGame) {

        textShort = i18n('game_perfectgame', 'Perfect Gm.');
        textLong = i18n('game_perfectgame_long', 'Perfect Game');
        type = 'perfect-game';
        url = mode === 'minisb' && mlbLinker && mlbLinker.getGamedayLink(data, true) || '';

    } else if (noHitter) {

        textShort = i18n('game_nohitter', 'No-Hitter');
        textLong = i18n('game_nohitter', 'No-Hitter');
        type = 'no-hitter';
        url = mode === 'minisb' && mlbLinker && mlbLinker.getGamedayLink(data, true) || '';
    }

    return {
        isFlag,
        textShort,
        textLong,
        type,
        url
    };

}

/**
 *
 * @function getGameClasses
 * @description creates game classes based on data
 * @param {String} mode
 * @param {Object} status
 * @returns {String}
 *
 */
function getGameClasses(component, mode = '', status = {}) {

    const { abstract = {} } = status;
    const { isCancelled, isPostponed, isPostSeasonReady } = abstract;
    const gameClass = `${component}__game`;

    let gameClasses = [
        gameClass,
        `${gameClass}--${mode}`,
        `${gameClass}--${status.abstractGameState}`
    ];

    if (isCancelled) {

        gameClasses.push(`${gameClass}--cancelled`);

    } else if (isPostponed) {

        gameClasses.push(`${gameClass}--postponed`);

    }

    if (isPostSeasonReady) {

        gameClasses.push(`${gameClass}--postseason`);

    }

    return gameClasses.join(' ');
}

/**
 *
 * @function getGameWrapperClass
 * @description creates class applied to gameWrapper
 * @param {Object} flag
 * @returns {String}
 *
 */
function getGameWrapperClass(component, flag = {}) {

    const gameWrapperClassBase = `${component}__game-wrapper`;
    let gameWrapperClass = gameWrapperClassBase;

    if (flag.type) {
        gameWrapperClass = `${gameWrapperClass} ${gameWrapperClassBase}--${flag.type}`;
    }

    return gameWrapperClass;
}

/**
 * @function getGameNote
 * @description
 * @param game
 * @returns {object}
 * @todo: Handle enhanced game
 * @todo: Handle playoff round ie. "NLDS 'A' Game 1" needs data source can't use description
 *
 */
function getGameNote(game = {}, mode = '', status = {}, component = '') {

    const seriesDescription = get(game, 'seriesDescription', '');
    const psShortDescription = get(game, 'seriesStatus.shortDescription', '');
    const psSeriesResult = get(game, 'seriesStatus.result', '');
    const { abstract } = status;
    let note = '';

    const isPostSeasonReady = abstract.isPostSeasonReady;

    if ((abstract.isAllStar || abstract.isExhibition || abstract.isSpring) && mode !== 'minisb') {

        note = seriesDescription;

    }

    if (isPostSeasonReady) {

        const hasShortDescription = psShortDescription && psShortDescription !== '';
        const hasSeriesResult = psSeriesResult && psSeriesResult !== '';
        const divider = hasShortDescription && hasSeriesResult && mode !== 'minisb' ? ' &#8226; ' : '';

        if (hasShortDescription) {
            note = `<span class='${component}__linescore__game-note--series'>${psShortDescription}${divider}</span>
                    <span class='${component}__linescore__game-note--series nowrap'>${psSeriesResult}</span>`;
        } else {
            note = `${psSeriesResult}`;
        }

    }

    const fgow = getGameOfTheWeek('facebook', game);

    if (fgow) {
        if (mode === 'minisb' || mode === 'responsive' || mode === 'MIG') {
            note = `<span class='${component}__linescore__game-note--series-gow ${component}__linescore__game-note--series-fb'><span class='full'>${i18n('button_fgow_L', 'MLB Live on Facebook Watch')}</span><span class='short'>${i18n('button_fgow_S', 'FB-WATCH')}</span></span>`;
        } else if (mode === 'game-selector') {
            note = `<span class='${component}__linescore__game-note--series-gow ${component}__linescore__game-note--series-fb'>${i18n('button_fgow', 'FB-WATCH')}</span>`;
        }
    }

    const youtubeGOW = getGameOfTheWeek('youtube', game);

    if (youtubeGOW) {
        if (mode === 'minisb' || mode === 'responsive' || mode === 'MIG') {
            note = `<span aria-label='${i18n('youtube_label', 'Live on YouTube - Watch Free')}' class='${component}__linescore__game-note--series-gow ${component}__linescore__game-note--series-youtube'><span class='full'>${i18n('youtube_label', 'Live on YouTube - Watch Free')}</span><span class='short'>${i18n('youtube_abbrv_label', 'YouTube')}</span></span>`;
        } else if (mode === 'game-selector') {
            note = `<span aria-label='${i18n('youtube_label', 'Live on YouTube - Watch Free')}' class='${component}__linescore__game-note--series-gow ${component}__linescore__game-note--series-youtube'>${i18n('youtube_abbrv_label', 'YouTube')}</span>`;
        }
    }

    // FGOTD overrides game type messages
    if (abstract.isFreeGame) {

        note = i18n('freegameoftheday', 'Free Game of the Day');

    }

    return note;

}

/**
 *
 * @param opts
 * @param stats
 * @returns {string}
 */
function getPitcherLiveStatLine(opts = {}, stats = []) {

    const { mode = 'responsive' } = opts;
    let statLine = '';
    let gameStats = getStat(stats, 'gameLog', 'pitching');
    let seasonStats = getStat(stats, 'statsSingleSeason', 'pitching');

    if (gameStats) {

        let sts = gameStats.stats;

        let { inningsPitched, pitchesThrown, strikes } = sts;

        if (inningsPitched !== undefined) {

            statLine = `${inningsPitched} ${i18n('stats_inningspitched_abbrev', 'IP')}`;

            if (mode !== 'MIG') {

                if ((pitchesThrown !== undefined) && (strikes !== undefined)) {

                    statLine = `${statLine} (${pitchesThrown}${i18n('stats_pitches_abbrev', 'P')} ${strikes}${i18n('stats_strikes_abbrev', 'S')})`;

                }
            }
        }
    }

    if (seasonStats) {

        let sts = seasonStats.stats;
        let { era } = sts;

        if ((era === undefined) || (era === '-')) {

            era = '-.--';

        }

        statLine = `${statLine ? statLine + ', ' : ''}${era} ${i18n('stats_earnedrunaverage_abbrev', 'ERA')}`;

    }

    return statLine;

}

/**
 *
 * @param opts
 * @param stats
 * @returns {string}
 */
function getBatterStatLine(opts, stats = []) {

    const { mode = 'responsive' } = opts;
    let statLine = '';
    let gameStats = getStat(stats, 'gameLog', 'hitting');
    let seasonStats = getStat(stats, 'statsSingleSeason', 'hitting');

    if (gameStats) {

        statLine = `${gameStats.stats.hits}-${gameStats.stats.atBats}`;

    }

    if (seasonStats) {

        statLine = `${statLine ? statLine + ', ' : ''}${seasonStats.stats.avg} ${i18n('stats_battingaverage_abbrev', 'AVG')}`;

        if (mode !== 'MIG') {

            statLine = `${statLine}, ${seasonStats.stats.ops} ${i18n('stats_onbaseplusslugging_abbrev', 'OPS')}`;

        }
    }

    return statLine;

}

/**
 * @function getDecisions
 * @desc Get a final game's decisions
 * @note Signature *must match* that of getProbables & getLiveGamePlayers and correspond to
 *       "modeStatusPlayersFunctions" in getPlayers
 * @param opts
 * @param gameData
 * @returns {Array} [winner, loser, <save>]
 */
function getDecisions(opts = {}, gameData = {}) {

    let { mlbLinker } = opts;
    let { decisions = {}, sport, teams } = gameData;
    const league = get(teams, 'home.team.league', {});
    let decisionNames = ['winner', 'loser', 'save'];
    let players = [];

    for (let i = 0, len = decisionNames.length; i < len; i = i + 1) {

        let decisionName = decisionNames[i];
        let player = decisions[decisionName];
        if (player) {
            let typeAbbrev = DECISION_TO_ABBREV[decisionName];
            const playerTeamId = get(player, 'currentTeam.id');
            const awayTeamId = get(teams, 'away.team.id');
            const homeTeamId = get(teams, 'home.team.id');

            // sometimes a player's current team is not up to date (if they're recently promoted/demoted) so check for both
            if (sport === 'milb' && (playerTeamId === awayTeamId || playerTeamId === homeTeamId)) {
                const teamAbbrev = awayTeamId === playerTeamId ? get(teams, 'away.team.abbreviation') : get(teams, 'home.team.abbreviation');
                typeAbbrev = `${DECISION_TO_ABBREV[decisionName]} &bull; ${teamAbbrev}`;
            }

            player.typeAbbrev = typeAbbrev;
            player.typeCode = `decision-${decisionName}`;
            player.statLine = getPitcherStatLine(opts, player, decisionName, sport);
            player.imageUrl = getPlayerImage(player, sport, opts.imgContext, league);
            player.playerUrl = getPlayerUrl(mlbLinker, player, sport);

            players.push(player);

        }

    }

    return players;

}

/**
 * @function getMatchup
 * @desc Get the current pitcher/batter matchup
 * @param opts
 * @param gameData
 * @returns {pitcher, batter}
 */
function getMatchup(opts = {}, gameData = {}) {
    const { linescore = {}, sport, teams } = gameData;
    const league = get(teams, 'home.team.league', {});
    const { defense = {}, offense = {} } = linescore;
    const { mlbLinker } = opts;
    const { batter } = offense;
    let { pitcher } = defense;

    if (!pitcher) {

        pitcher = {
            boxscoreName: i18n('scores_pitchingChange', 'Pitching Change')
        };

    }

    pitcher.typeAbbrev = i18n('game_position_p_abbrev', 'P');
    pitcher.typeCode = 'pitcher';
    pitcher.statLine = getPitcherLiveStatLine(opts, pitcher.stats);
    pitcher.imageUrl = getPlayerImage(pitcher, sport, opts.imgContext, league);
    pitcher.playerUrl = getPlayerUrl(mlbLinker, pitcher, sport);

    if (batter) {

        batter.typeAbbrev = i18n('stats_atbat', 'AB');
        batter.typeCode = 'at-bat';
        batter.statLine = getBatterStatLine(opts, batter.stats);
        batter.imageUrl = getPlayerImage(batter, sport, opts.imgContext, league);
        batter.playerUrl = getPlayerUrl(mlbLinker, batter, sport);
    }

    return {
        pitcher,
        batter
    };
}

/**
 * @function getLiveGamePlayers
 * @desc Get the current matchup (and last play if in responsive mode)
 * @note Signature *must match* that of getProbables & getDecisions and correspond to
 *       "modeStatusPlayersFunctions" in getPlayers
 * @param opts
 * @param gameData
 * @returns {Array} [pitcher, batter, <previousPlay>]
 */
function getLiveGamePlayers(opts = {}, gameData = {}) {

    let { mode = 'responsive' } = opts;
    let players = [];
    let matchup = getMatchup(opts, gameData);
    const { sport, teams } = gameData;

    const awayTeamId = get(teams, 'away.team.id');
    const homeTeamId = get(teams, 'home.team.id');
    const awayTeamAbbrev = get(teams, 'away.team.abbreviation');
    const homeTeamAbbrev = get(teams, 'home.team.abbreviation');

    if (matchup.pitcher) {
        if (sport === 'milb') {
            const pitcherTeamId = get(matchup, 'pitcher.currentTeam.id');
            let typeAbbrev = 'Pitching';
            if (pitcherTeamId === awayTeamId || pitcherTeamId === homeTeamId) {
                const teamAbbrev = awayTeamId === pitcherTeamId ? awayTeamAbbrev : homeTeamAbbrev;
                typeAbbrev = `Pitching &bull; ${teamAbbrev}`;
            }
            matchup.pitcher.typeAbbrev = typeAbbrev;
        }
        players.push(matchup.pitcher);

    }

    if (matchup.batter) {
        if (sport === 'milb') {
            const batterTeamId = get(matchup, 'batter.currentTeam.id');
            let typeAbbrev = 'At Bat';
            if (batterTeamId === awayTeamId || batterTeamId === homeTeamId) {
                const teamAbbrev = awayTeamId === batterTeamId ? awayTeamAbbrev : homeTeamAbbrev;
                typeAbbrev = `At Bat &bull; ${teamAbbrev}`;
            }
            matchup.batter.typeAbbrev = typeAbbrev;
        }
        players.push(matchup.batter);

    }

    if (mode === 'responsive') {

        let previousPlay = getPreviousPlay(opts, gameData);

        if (previousPlay) {

            players.push(previousPlay);

        }

    }

    return players;

}

/**
 * @function getPlayers
 * @desc Get an array of players to display headshots/names/statlines for
 * probables, matchup (with/without last play) & decisions
 * @note modeStatusPlayersFunctions must all share the same signature
 * @param opts
 * @param gameData
 * @param sport
 * @returns {Array} Array of player objects ordered according to what "group" is returned
 */
function getPlayers(opts = {}, gameData = {}) {

    const { mode = 'responsive' } = opts;
    const { status = {} } = gameData;
    const { abstractGameCode = '' } = status;
    const modeStatusPlayersFunctions = {
        responsive: {
            P: getProbables,
            L: getLiveGamePlayers,
            F: getDecisions
        },
        MIG: {
            P: getProbables,
            L: getLiveGamePlayers,
            F: getDecisions
        },
        mediawall: {
            P: getProbables
        }
    };

    let players = [];

    let statusPlayersFunctions = modeStatusPlayersFunctions[mode];

    if (statusPlayersFunctions) {

        let playersFunction = statusPlayersFunctions[abstractGameCode];

        if (playersFunction) {

            players = playersFunction(opts, gameData);

        }

    }

    return players;

}

/**
 * @function getMode
 * @description
 * @param mode
 * @returns {{isGameSelector: boolean}}
 */
function getMode(mode = '') {

    return {
        isResponsive: mode === 'responsive',
        isGameSelector: mode === 'game-selector',
        isMinisb: mode === 'minisb',
        isMIG: mode === 'MIG',
        isMediawall: mode === 'mediawall',
        showHitsAndErrors: mode === 'MIG' || mode === 'responsive' || mode === 'mediawall'
    };

}

/**
 * @function getSport
 * @description
 * @param sportId
 * @returns {string}
 */
function getSport(sportId, milbSportIds = '') {

    const id = parseInt(sportId, 10);
    const isMilb = (milbSportIds.indexOf(id) !== -1);

    return isMilb ? 'milb' : 'mlb';

}

/**
 * @function getBroadcasterInfo
 * @description
 * @param statusCode
 * @returns {string}
 */
function getBroadcasterInfo(statusCode = '') {

    let broadcastLabel = '';

    if (statusCode && statusCode !== '') {
        if (statusCode === 'S'
            || statusCode === 'P'
            || (statusCode.length === 2 && statusCode !== 'PW' && statusCode.indexOf('P') === 0)) {

            broadcastLabel = i18n('ps_scores_watch_on', 'Watch On');

        } else if (statusCode === 'PW'
            || statusCode === 'I'
            || statusCode === 'IZ'
            || statusCode === 'IH'
            || statusCode.indexOf('M') === 0
            || statusCode.indexOf('N') === 0
            || statusCode.indexOf('UZ') === 0) {

            broadcastLabel = i18n('ps_scores_watch_live', 'Watch Live');

        } else if ((statusCode.length === 2 && statusCode !== 'IH' && statusCode.indexOf('I') === 0)
            || statusCode.indexOf('T') === 0
            || (statusCode !== 'UZ' && statusCode.indexOf('U') === 0)) {

            broadcastLabel = '';

        }
    }

    return broadcastLabel;

}

/**
 * Prioritize national, then home broadcast sources.
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
const byBroadcastRelevance = (a, b) => {
    return (b.national - a.national) || (b.home - a.home);
};

/**
 * There is an additional requirement to not list the away broadcasts for club MIG,
 * and also to order the broadcasts National, Home, Away otherwise.
 * @param {Array} broadcasts
 * @param {object} [game]
 * @param {number|string|null} teamId
 * @returns {{tv: (string), radio: (string), hasBroadcasts: boolean}}
 *
 * @todo How to tell if a broadcast is national.
 */
function getBroadcasts(broadcasts = [], game = {}, teamId = null) {

    const broadcasters = [142, 144, 5035, 4725, 5040, 129, 2661];
    const { status } = game;
    const { statusCode, abstract } = status;
    let tvBroadcasts = {};
    let radioBroadcasts = {};
    let broadcasterInfo = {};
    let broadcastsObj = {};

    for (let i = 0; i < broadcasts.length; ++i) {
        const broadcast = broadcasts[i];
        const { id, name, type } = broadcast;

        let container;

        if (type === 'TV') {
            container = tvBroadcasts;
        } else {
            container = radioBroadcasts;
        }

        container[name] = container[name] || {
            name,
            home: false,
            away: false
        };
        const source = container[name];
        source[broadcast.homeAway] = true;

        if (source.home && source.away) {
            source.national = true;
        }

        if (type === 'TV' && abstract.isPostSeasonReady) {
            broadcasters.forEach(function (_id) {
                if (_id === id) {
                    broadcasterInfo.id = id;
                    broadcasterInfo.label = getBroadcasterInfo(statusCode);
                    broadcasterInfo.className = 'id-' + id;
                }
            });
        }
    }

    /** @type {function<boolean>} */
    let contextual = () => {

        return true;

    };

    if (game && game.teams) {

        let homeId = get(game, 'teams.home.team.id');
        let awayId = get(game, 'teams.away.team.id');

        if (Number(homeId) === Number(teamId)) {

            contextual = (source) => {

                return source.home || source.national;
            };

        }
        if (Number(awayId) === Number(teamId)) {

            contextual = (source) => {

                return source.away || source.national;

            };

        }
    }

    tvBroadcasts = Object.keys(tvBroadcasts)
        .map((key) => tvBroadcasts[key])
        .sort(byBroadcastRelevance)
        .filter(contextual);
    radioBroadcasts = Object.keys(radioBroadcasts)
        .map((key) => radioBroadcasts[key])
        .sort(byBroadcastRelevance)
        .filter(contextual);

    /** @type {function<string>} */
    const name = (source) => {

        return source.name;

    };

    const isBroadcasterInfo = Object.keys(broadcasterInfo).length > 0;
    const hasBroadcasts = (tvBroadcasts.length || radioBroadcasts.length || isBroadcasterInfo) && game.sport !== 'milb';

    broadcastsObj = {
        tv: tvBroadcasts.map(name).join(', '),
        radio: radioBroadcasts.map(name).join(', '),
        hasBroadcasts: hasBroadcasts
    };

    if (isBroadcasterInfo) {
        broadcastsObj.broadcasterInfo = broadcasterInfo;
    }

    return broadcastsObj;
}

/**
 * @function getButtonOptions
 * @param opts
 * @returns {*}
 */
function getButtonOptions(opts = {}) {

    const { mode } = opts;

    let buttonOpts = {
        variant: 'secondary'
    };

    return buttonOpts;

}

/**
 * @function getPreviousPlay
 * @desc Get the last play of the game, represented as a player object
 * @param opts
 * @param gameData
 * @returns {Object|null} Player object representing last play (id,image,url)
 * @todo Re-examine player image & link (always uses batter)
 */
function getPreviousPlay(opts = {}, gameData = {}) {

    let prevPlay = get(gameData, 'previousPlay.result.description', null);
    let prevPlayer = null;
    let { sport, teams } = gameData;
    const league = get(teams, 'home.team.league', {});

    if (prevPlay) {

        let { mlbLinker } = opts;
        prevPlayer = get(gameData, 'previousPlay.matchup.batter', null);

        if (prevPlayer) {
            prevPlayer.typeAbbrev = i18n('scores_lastplay', 'Last play');
            prevPlayer.typeCode = 'last-play';
            prevPlayer.statLine = prevPlay;
            prevPlayer.imageUrl = getPlayerImage(prevPlayer, sport, opts.imgContext, league);
            prevPlayer.playerUrl = getPlayerUrl(mlbLinker, prevPlayer, sport);
        }

    }

    return prevPlayer;

}

/**
 * @function getProbable
 * @desc Get a player object to represent a probable pitcher
 * @param opts
 * @param team
 * @return {Object} Object representing a player or TBD when probable hasn't been set
 */
function getProbable(opts = {}, team = {}, sport = 'mlb', league = {}) {

    let { mlbLinker } = opts;
    let { probablePitcher = {}, isMilb } = team;

    probablePitcher.typeAbbrev = team.team.abbreviation;
    probablePitcher.typeCode = 'probable';
    probablePitcher.statLine = getPitcherStatLine(opts, probablePitcher);
    probablePitcher.isTBD = !probablePitcher.boxscoreName;
    probablePitcher.boxscoreName = probablePitcher.boxscoreName || 'TBD';
    probablePitcher.imageUrl = getPlayerImage(probablePitcher, sport, opts.imgContext, league);
    probablePitcher.playerUrl = getPlayerUrl(mlbLinker, probablePitcher, sport);

    return probablePitcher;
}

/**
 * @function getProbables
 * @desc Get a preview game's probable pitchers
 * @note Signature *must match* that of getMatchup & getDecisions and correspond to
 *       "modeStatusPlayersFunctions" in getPlayers
 * @param opts
 * @param gameData
 * @returns {Array} [away, home]
 */
function getProbables(opts = {}, gameData = {}) {

    const { mode = 'responsive' } = opts;
    const { teams = {}, sport } = gameData;
    let { away, home } = teams;
    const league = get(teams, 'home.team.league', {});

    away = getProbable(opts, away, sport, league);
    home = getProbable(opts, home, sport, league);

    // (Both are TBD or otherwise unavailable)
    if ((away.imageUrl === home.imageUrl) && (mode !== 'MIG')) {
        away.ghostbusters = home.ghostbusters = true;
    }

    return [
        away,
        home
    ];
}

/**
 * @function getLeader
 * @decription get a team's leader for a given category
 * @param teamLeaders {Array} Array of teamLeaders from statsapi
 * @param leaderCategory {String} Category for which leader to be found
 * @param statGroup {String} 'hitting' or 'pitching'
 * @param label {String} Label used to match the leaderCategory
 * @returns {Object|null} Object representing person with category, boxscoreName and value.
 *          Returns null if category isn't in teamLeaders array
 */
function getLeader(opts = {}, teamLeaders = [], leaderCategory = '', statGroup = 'hitting', label = '', sport = 'mlb') {

    let teamLeaderInCategory = null;

    for (let i = 0, len = teamLeaders.length; i < len; i = i + 1) {

        let teamLeader = teamLeaders[i];
        if ((teamLeader.leaderCategory === leaderCategory) && (teamLeader.statGroup === statGroup)) {

            teamLeaderInCategory = teamLeader;
            break;

        }
    }

    if (teamLeaderInCategory) {

        let { leaders } = teamLeaderInCategory;
        let leader = leaders[0];
        let { value } = leader;

        //TODO: Need boxscore name
        let { mlbLinker } = opts;
        let name = leader.person.fullName.split(' ');
        if (name[0].charAt(1) !== '.') {

            name[0] = name[0].charAt(0) + '.';

        }
        let boxscoreName = name.join(' ');
        let playerUrl = getPlayerUrl(mlbLinker, leader.person, sport);

        if (leaders.length > 1) {

            boxscoreName = i18n('scores_tied_leaders', '{{numPlayers}} Tied at').replace('{{numPlayers}}', leaders.length);
            playerUrl = null;

        }

        return {
            category: label,
            boxscoreName,
            playerUrl,
            value
        };

    }

    return null;

}

/**
 * @function getLeaders
 * @decription get avg, homerun and win leaders for a given team
 * @param team team.team data from statsapi
 * @returns {*}
 */
function getLeaders(opts = {}, team = {}, sport = 'mlb') {

    let { teamLeaders } = team;

    if (teamLeaders && teamLeaders.length) {

        const avgAbbrev = i18n('stats_battingaverage_abbrev', 'AVG');
        const hrAbbrev = i18n('stats_homerun_abbrev', 'HR');
        const rbiAbbrev = i18n('stats_runsbattedin_abbrev', 'RBI');

        return {
            avg: getLeader(opts, teamLeaders, 'battingAverage', 'hitting', avgAbbrev, sport),
            hr: getLeader(opts, teamLeaders, 'homeRuns', 'hitting', hrAbbrev, sport),
            rbi: getLeader(opts, teamLeaders, 'runsBattedIn', 'hitting', rbiAbbrev, sport)
        };

    }

    return null;

}

/**
 * @function getTeamLeaders
 * @decription get avg, homerun and win leaders for both teams
 * @param teams Teams node of game data returned from statsapi
 * @returns {*}
 */
function getTeamLeaders(opts = {}, teams = {}, sport = 'mlb') {

    let away = getLeaders(opts, teams.away.team, sport);
    let home = getLeaders(opts, teams.home.team, sport);

    if (away && home) {

        return {
            away,
            home
        };

    }

    return null;

}

//TODO: handle LLI (https://jira.mlbam.com/browse/SSSS-3138)
/**
 * @function getVideoId
 * @description get the video content-id from
 * @param data
 * @returns {string}
 */
function getVideoId(data = {}) {

    // If live lookin, get different endpoint
    let id = get(data, 'content.media.featuredMedia.id');

    return id;

}

/**
 * @function getGameOfTheWeek
 * @description get game of the week
 * @param product
 * @param data
 * @returns {boolean}
 */
function getGameOfTheWeek(product, game = {}) {
    const epg = get(game, 'content.media.epg');
    let showGOW = false;

    if (epg && epg.length > 0) {

        for (let i = epg.length; i--;) {

            if (epg[i].title === 'MLBTV' && epg[i].items) {
                let items = epg[i].items;

                for (let j = items.length; j--;) {

                    if (items[j][product] && items[j][product].fgow === true) {
                        showGOW = true;
                        break;
                    }

                }

            }

        }

    }

    return showGOW;

}

/**
 * @function getGameTypeDesc
 * @description finds description for the gametypes
 * @param {string} gameType
 */
function getGameTypeDesc(gameType) {
    const gameTypes = {
        E: 'Exhibition',
        S: 'Spring Training',
        R: 'Regular season',
        A: 'All-Star game',
        F: 'Wild Card Game',
        D: 'Division Series',
        L: 'League Championship Series',
        W: 'World Series'
    };

    const gameTypeDesc = gameTypes[gameType];

    if (gameTypeDesc) {
        return gameTypeDesc;
    }

    return '';
}

/**
 * @function getGameTracking
 * @param game
 * @returns {{awayId: *, homeId: *, date: string, type: *, scenario: *, description: *}}
 */
function getGameTracking(game = {}) {

    let gameDate = get(game, 'gameDate', '');
    let awayId = get(game, 'teams.away.team.id', '');
    let homeId = get(game, 'teams.home.team.id', '');
    let yyyymmdd = gameDate.length > 10 ? gameDate.substr(0, 10) : '';
    let type = get(game, 'seriesDescription', '');
    let scenario = get(game, 'status.abstractGameState', '');
    let description = get(game, 'status.detailedState', '');

    return {
        awayId: awayId,
        homeId: homeId,
        date: yyyymmdd,
        type: type,
        scenario: scenario,
        description: description
    };
}

/**
 * @function getGameSponsor
 * @param opts
 * @param mode
 * @param game
 * @returns {*}
 */
function getGameSponsor(opts = {}, mode, game = {}) {
    let lang = opts.lang || 'en';
    let sponsor;
    const sportId = (opts.sportId) ? opts.sportId.toString() : '';
    const sportIdList = (sportId !== '') ? sportId.split(',') : [];
    const isMLB = ((sportIdList.length === 0) || (sportIdList.indexOf(SPORTID_MLB) > -1));

    const postseasonSponsorMap = {
        D1: `${lang}-ALDS-sponsor`,
        D2: `${lang}-ALDS-sponsor`,
        D3: `${lang}-NLDS-sponsor`,
        D4: `${lang}-NLDS-sponsor`,
        L1: `${lang}-LCS-sponsor`,
        L2: `${lang}-LCS-sponsor`,
        WC: `${lang}-WC-sponsor`,
        WS: `${lang}-WS-sponsor`
    };

    const gameType = get(game, 'gameType');
    const seriesNumber = get(game, 'teams.home.seriesNumber');
    const showSponsor = (isMLB) && (mode === 'responsive' || mode === 'MIG');

    if (!showSponsor) {
        return null;
    }

    if (gameType === 'D' || gameType === 'L') {
        sponsor = postseasonSponsorMap[`${gameType}${seriesNumber}`];
    } else if (gameType === 'W') {
        sponsor = postseasonSponsorMap.WS;
    } else if (gameType === 'F') {
        sponsor = postseasonSponsorMap.WC;
    }

    return sponsor;

}

/**
 * @function getPromotion
 * @param opts
 * @param game
 * @returns {*}
 */
function getPromotion(opts = {}, game = {}) {

    const { status, sport, gameDate = '' } = game;
    const { abstract } = status;
    const { isPreview } = abstract;
    const { mode, teamId = '000' } = opts;

    let homeId = get(game, 'teams.home.team.id');
    let homeLongName = document.querySelector("meta[name='context-url-prefix']");
    let homeShortName = get(game, 'teams.home.team.shortName').replace(/\s+/g, '-').toLowerCase();
    let homeName = homeLongName ? homeLongName.getAttribute("content").replace('/', '') : homeShortName;
    let isHome = parseInt(homeId, 10) === parseInt(teamId, 10);
    let month = gameDate.length > 10 ? parseInt(gameDate.substr(5, 2), 10) : '';

    let promotion = null;
    let promotions = get(game, 'promotions', []);
    let gamePk = get(game, 'gamePk');

    if (isPreview && mode === 'MIG' && sport === 'milb' && promotions.length > 0 && isHome) {

        promotion = promotions[0];
        promotion.label = i18n('schedule_promotion', 'Promotion');
        promotion.truncated = helpers.truncate(promotion.description || '', 85);
        promotion.url = `https://www.milb.com/${homeName}/tickets/promotions#game=${gamePk}`;
        // promote editor returns "imageUrl": "undefined" when there is no image.
        promotion.imageUrl = promotion.imageUrl.startsWith('/') || promotion.imageUrl.startsWith('http') ? promotion.imageUrl : null;
    }

    return promotion;

}


/**
 * @function getPostseasonInfo
 * @param opts
 * @param game
 * @returns {*}
 */
function getPostseasonInfo(game = {}) {

    const { seriesStatus } = game;
    const { description, gameNumber, result, shortDescription } = seriesStatus;

    if (!description || !gameNumber) {
        return null;
    }

    return {
        description,
        gameNumber,
        result,
        shortDescription
    };
}

/**
 * @function hasMiniButtonsSm
 * @param buttons Array of BamButtons
 * @returns {boolean}
 */
function hasMiniButtonsSm(buttons = [], flag = {}, device = 'desktop') {

    if ((device === 'desktop') && (flag.type === 'live-lookin')) {

        return true;

    }

    for (let i = 0, len = buttons.length; i < len; i = i + 1) {

        let button = buttons[i];

        if (!button.hideInMiniSm) {

            return true;

        }
    }

    return false;

}

export default {
    getBroadcasts,
    getButtonOptions,
    getDate,
    getDescription,
    getFlag,
    getGameClasses,
    getGameWrapperClass,
    getGameNote,
    getGameTime,
    getHomeRuns,
    getMode,
    getSport,
    getPlayers,
    getTeamLeaders,
    getVideoId,
    getGameTracking,
    getGameSponsor,
    getPromotion,
    getGameTypeDesc,
    getChallengingTeam,
    hasMiniButtonsSm,
    getGameOfTheWeek,
    getGameTimePostseasonPush,
    getPostseasonInfo
};
